/**
 * Video Player Controls Component
 * Modern player controls with segment-based timeline for multi-slide videos
 * Matches the visual style of GsapPlayerControls
 */

import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, RotateCcw, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { VideoPlayerControlsProps } from './PlayerTypes';

export const VideoPlayerControls: React.FC<VideoPlayerControlsProps> = ({
  isPlaying,
  currentTime,
  duration,
  currentSlide,
  segments,
  playbackRate: _playbackRate, // Reserved for future speed control UI
  onPlayPause,
  onRestart,
  onSeek,
  onSlideChange,
  onPreviousSlide,
  onNextSlide,
  onSpeedChange: _onSpeedChange, // Reserved for future speed control UI
}) => {
  const [hoverTime, setHoverTime] = useState<number | null>(null);
  const [hoverPosition, setHoverPosition] = useState(0);
  const [hoverSegment, setHoverSegment] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const progressRef = useRef<HTMLDivElement>(null);
  const wasPlayingRef = useRef(false);

  // Format time helper
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get segment at given time
  const getSegmentAtTime = (time: number): number => {
    for (let i = 0; i < segments.length; i++) {
      if (time >= segments[i].startTime && time < segments[i].endTime) {
        return i;
      }
    }
    return segments.length - 1; // Last segment
  };

  // Handle timeline hover for preview
  const handleTimelineHover = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressRef.current || isDragging) return;

    const rect = progressRef.current.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const time = Math.max(0, Math.min(duration, percent * duration));
    const segmentIndex = getSegmentAtTime(time);

    setHoverTime(time);
    setHoverPosition(e.clientX - rect.left);
    setHoverSegment(segmentIndex);
  };

  const handleTimelineLeave = () => {
    if (!isDragging) {
      setHoverTime(null);
      setHoverSegment(null);
    }
  };

  // Handle segment click
  const handleSegmentClick = (segmentIndex: number) => {
    onSlideChange(segmentIndex);
  };

  // Handle scrubbing
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressRef.current || !duration) return;
    e.preventDefault();
    e.stopPropagation();

    const rect = progressRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percent = Math.max(0, Math.min(1, clickX / rect.width));
    const time = percent * duration;

    setIsDragging(true);
    wasPlayingRef.current = isPlaying;

    // Pause during scrubbing
    if (isPlaying) {
      onPlayPause();
    }

    // Perform initial seek
    onSeek(time);
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!progressRef.current) return;

      const rect = progressRef.current.getBoundingClientRect();
      const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const time = percent * duration;

      onSeek(time);
      setHoverTime(time);
      setHoverPosition(Math.max(0, Math.min(rect.width, e.clientX - rect.left)));
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setHoverTime(null);
      setHoverSegment(null);

      // Resume playback if it was playing before
      if (wasPlayingRef.current && !isPlaying) {
        onPlayPause();
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, duration, onSeek, isPlaying, onPlayPause]);

  return (
    <div className={cn(
      "bg-card/95 backdrop-blur-md border-t border-border/50",
      "px-4 py-2"
    )}>
      {/* Single Row Layout - All controls in one line */}
      <div className="flex items-center gap-3">
        {/* Play/Pause Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onPlayPause}
          className="h-8 w-8 transition-all hover:scale-110 flex-shrink-0"
          title={isPlaying ? "Pause (Space)" : "Play (Space)"}
        >
          {isPlaying ? (
            <Pause className="h-4 w-4" />
          ) : (
            <Play className="h-4 w-4 ml-0.5" />
          )}
        </Button>

        {/* Current Time */}
        <span className="text-muted-foreground text-xs font-mono w-10 text-right flex-shrink-0">
          {formatTime(currentTime)}
        </span>

        {/* Timeline Progress Bar */}
        <div
          ref={progressRef}
          className="relative flex-1 h-8 flex items-center cursor-pointer group"
          onMouseMove={handleTimelineHover}
          onMouseLeave={handleTimelineLeave}
          onMouseDown={handleMouseDown}
        >
          {/* Segment containers */}
          <div className="absolute inset-x-0 h-1.5 flex gap-0.5 group-hover:h-2 transition-all">
            {segments.map((segment, index) => {
              const segmentWidth = (segment.duration / duration) * 100;
              const segmentProgress = Math.max(
                0,
                Math.min(
                  100,
                  ((currentTime - segment.startTime) / segment.duration) * 100
                )
              );
              const isActive = currentSlide === index;
              const isPast = currentTime > segment.endTime;
              const isHover = hoverSegment === index;

              return (
                <div
                  key={segment.slideId}
                  className={cn(
                    "relative h-full rounded-sm transition-all duration-200",
                    "bg-muted",
                    isHover && "bg-muted/60"
                  )}
                  style={{ width: `${segmentWidth}%` }}
                  onClick={() => handleSegmentClick(index)}
                >
                  {/* Filled progress for this segment */}
                  <div
                    className={cn(
                      "absolute left-0 h-full rounded-sm transition-all",
                      isPast ? "bg-primary" : isActive ? "bg-primary" : "bg-primary/40"
                    )}
                    style={{
                      width: isPast ? '100%' : isActive ? `${segmentProgress}%` : '0%'
                    }}
                  />

                  {/* Segment label on hover */}
                  {isHover && (
                    <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-popover text-popover-foreground text-xs px-2 py-1 rounded border border-border shadow-md whitespace-nowrap z-50 pointer-events-none">
                      Slide {index + 1}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Global progress handle */}
          <div
            className={cn(
              "absolute top-1/2 w-2.5 h-2.5 bg-primary rounded-full transform -translate-y-1/2 -translate-x-1/2 z-10",
              "transition-all group-hover:scale-125 shadow-lg border border-background",
              "opacity-0 group-hover:opacity-100",
              isDragging && "scale-150 opacity-100"
            )}
            style={{ left: `${(currentTime / duration) * 100}%` }}
          />

          {/* Hover Time Tooltip */}
          {hoverTime !== null && (
            <div
              className="absolute -top-8 bg-popover text-popover-foreground text-xs px-2 py-1 rounded border border-border shadow-md pointer-events-none z-50"
              style={{
                left: hoverPosition,
                transform: 'translateX(-50%)'
              }}
            >
              {formatTime(hoverTime)}
            </div>
          )}
        </div>

        {/* Duration */}
        <span className="text-muted-foreground text-xs font-mono w-10 flex-shrink-0">
          {formatTime(duration)}
        </span>

        {/* Divider */}
        <div className="w-px h-5 bg-border/50 flex-shrink-0" />

        {/* Navigation Controls */}
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={onPreviousSlide}
            disabled={currentSlide === 0}
            className="h-7 w-7 transition-all hover:scale-110 disabled:opacity-30"
            title="Previous Slide (←)"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={onNextSlide}
            disabled={currentSlide === segments.length - 1}
            className="h-7 w-7 transition-all hover:scale-110 disabled:opacity-30"
            title="Next Slide (→)"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>

        {/* Restart Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onRestart}
          className="h-7 w-7 transition-all hover:scale-110 flex-shrink-0"
          title="Restart (R)"
        >
          <RotateCcw className="h-3.5 w-3.5" />
        </Button>

        {/* Divider */}
        <div className="w-px h-5 bg-border/50 flex-shrink-0" />

        {/* Slide Counter */}
        <span className="text-muted-foreground text-xs font-medium flex-shrink-0">
          {currentSlide + 1}/{segments.length}
        </span>
      </div>
    </div>
  );
};
